import {
  type QBankStructureLevel,
  type GetQBankStructureLevelItemsWithQIdByQBankStructureIdQuery,
  QBankStructureLevelCategory,
} from '@exam-management/graphql';
import { randomUUID } from '@shared/utils';

import { type QBankStructureSettingSectionTableData } from '../../components';

export type QBankStructureLevelItemWithLevel =
  GetQBankStructureLevelItemsWithQIdByQBankStructureIdQuery['qbsGetQBankStructureLevelItemsByQBankStructureId'][0] & {
    qBankStructureLevel: QBankStructureLevel;
    isNew?: boolean;
  };

const mapQBankCategoryToLabel = {
  [QBankStructureLevelCategory.Course]: 'หลักสูตร',
  [QBankStructureLevelCategory.Part]: 'หมวด',
  [QBankStructureLevelCategory.Subject]: 'วิชา',
  [QBankStructureLevelCategory.Group]: 'เล่ม/วิชา',
  [QBankStructureLevelCategory.Chapter]: 'บท',
  [QBankStructureLevelCategory.Topic]: 'หัวข้อ',
  [QBankStructureLevelCategory.SubTopic]: 'หัวข้อย่อย',
};

export function sortLevelItems(
  data: QBankStructureSettingSectionTableData[],
  parentId: string | null,
  result: QBankStructureSettingSectionTableData[] = [],
) {
  const children = data
    .filter((item) => {
      return item.parentId === parentId;
    })
    .sort((a, b) => a.order - b.order);
  for (const child of children) {
    result.push(child);
    sortLevelItems(data, child.id, result);
  }

  return result;
}

export function findChildrenIds(
  data: {
    id: string;
    level: QBankStructureLevelCategory;
    order: number;
    parentId: string | null;
    levelName: string;
    qSourceName: string;
    parentIds: string[];
  }[],
  parentId: string | null,
  childrenIds: string[] = [],
): string[] {
  const children = data.filter((i) => i.parentId === parentId);
  for (const child of children) {
    childrenIds.push(child.id);
    findChildrenIds(data, child.id, childrenIds);
  }
  return childrenIds;
}

function findParentIds(
  data: QBankStructureLevelItemWithLevel[],
  parentId: string | null,
  parentIds: string[] = [],
): string[] {
  if (!parentId) return parentIds;
  const parent = data.find((i) => i.id === parentId);
  if (parent) return findParentIds(data, parent.parentLevelItemId, [...parentIds, parent.id]);
  return parentIds;
}

export function mapSelectedLevelItems(
  selectedLevelItemsWithLevel: QBankStructureLevelItemWithLevel[],
  allLevelItems: QBankStructureLevelItemWithLevel[],
) {
  const structuredItems = buildClosedLevelItems(selectedLevelItemsWithLevel, allLevelItems);

  const result = structuredItems.map((item) => ({
    id: item.id,
    level: item.qBankStructureLevel.category,
    order: item.order,
    parentId: item.parentLevelItemId,
    levelName:
      item.tag?.name ?? `${mapQBankCategoryToLabel[item.qBankStructureLevel.category]}: ไม่ระบุ`,
    qSourceName: item.qId ?? '',
    parentIds: findParentIds(structuredItems, item.parentLevelItemId),
  }));
  return result;
}

export function buildClosedLevelItems(
  selectedLevelItemsWithLevel: QBankStructureLevelItemWithLevel[],
  allLevelItems: QBankStructureLevelItemWithLevel[],
): QBankStructureLevelItemWithLevel[] {
  const resultMap = new Map<string, QBankStructureLevelItemWithLevel>();

  const levelsOrdering = [
    QBankStructureLevelCategory.Chapter,
    QBankStructureLevelCategory.Group,
    QBankStructureLevelCategory.Subject,
    QBankStructureLevelCategory.Part,
    QBankStructureLevelCategory.Course,
  ];

  const selectedLevelItemIdsSet = [...allLevelItems]
    .sort((a, b) => {
      return (
        levelsOrdering.indexOf(a.qBankStructureLevel.category) -
        levelsOrdering.indexOf(b.qBankStructureLevel.category)
      );
    })
    .reduce(
      (acc, item) => {
        if (acc.has(item.id) && item.parentLevelItemId) {
          acc.add(item.parentLevelItemId);
        }
        if (!item.parentLevelItemId) {
          acc.add(item.id);
        }
        return acc;
      },
      new Set<string>(selectedLevelItemsWithLevel?.map((item) => item.id) ?? []),
    );

  const selectedLevelItems = allLevelItems.filter((item) => selectedLevelItemIdsSet.has(item.id));

  const accumulated = new Map<string, QBankStructureLevelItemWithLevel>();

  for (const [index, level] of levelsOrdering.entries()) {
    const possibleParentsMap = new Map<string, QBankStructureLevelItemWithLevel>(
      selectedLevelItems
        .filter((item) => item.qBankStructureLevel.category === levelsOrdering[index + 1])
        .map((item) => [item.id, item]),
    );

    const currentLevelItems = [...selectedLevelItems, ...accumulated.values()].filter(
      (item) => item.qBankStructureLevel.category === level,
    );

    for (const item of currentLevelItems) {
      if (!item.parentLevelItemId || possibleParentsMap.has(item.parentLevelItemId)) {
        resultMap.set(item.id, {
          ...item,
          parentLevelItemId: possibleParentsMap.get(item.parentLevelItemId ?? '')?.id ?? null,
        });
      } else {
        // Need to create a parent item
        const parentItem = {
          id: `off-level-${item.parentLevelItemId}-${level}-${item.id}`,
          parentLevelItemId: item.parentLevelItemId,
          tag: {
            id: randomUUID(),
            name: `${mapQBankCategoryToLabel[levelsOrdering[index + 1]]}: ไม่เปิดใช้งาน`,
          },
          qBankStructureLevel: {
            id: randomUUID(),
            category: levelsOrdering[index + 1],
          },
          qBankStructureId: item.qBankStructureId,
          tagId: null,
          order: 0,
          qBankStructureLevelId: randomUUID(),
        } as QBankStructureLevelItemWithLevel;

        possibleParentsMap.set(parentItem.parentLevelItemId ?? '', parentItem);
        accumulated.set(parentItem.id, parentItem);
        resultMap.set(item.id, {
          ...item,
          parentLevelItemId: parentItem.id,
        });
      }
    }
  }
  return Array.from(resultMap.values());
}
